import React, { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Loader2, Mail, CheckCircle2, XCircle, ExternalLink, AlertTriangle, CreditCard, Repeat, Banknote } from 'lucide-react'
import { fetchFarmInformation } from '../../supabase-client'
import { parseDateString } from '../../utils/dateUtils'

/**
 * InvoiceEmailer Component
 *
 * Displays a preview of the invoice email that will be sent to customers
 * with multiple payment options and clear payment instructions.
 *
 * Features:
 * - Responsive HTML email template
 * - Company branding (Caledonia Mushrooms)
 * - Invoice details summary
 * - Multiple payment CTAs (Xero online payment, bank transfer)
 * - Payment instructions
 * - WCAG 2.1 AA accessibility compliance
 *
 * @param {Object} props
 * @param {Object} props.invoice - Invoice object with all details
 * @param {Object} props.customer - Customer object with email and name
 * @param {Object} props.branch - Branch object with customer_type (migrated from customer level)
 * @param {Function} props.onSendEmail - Callback function to send email
 * @param {boolean} props.isSending - Whether email is currently being sent
 * @param {Object} props.sendStatus - Status of email send operation {success: boolean, error: string}
 */
export default function InvoiceEmailer({ invoice, customer, branch, onSendEmail, isSending = false, sendStatus = null }) {
  const [farmInfo, setFarmInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showConfirmation, setShowConfirmation] = useState(false) // EM-M2: Confirmation dialog

  useEffect(() => {
    loadFarmInformation()
  }, [])

  const loadFarmInformation = async () => {
    try {
      setLoading(true)
      const info = await fetchFarmInformation()
      if (info) {
        setFarmInfo(info)
      } else {
        setError('Farm information not found')
      }
    } catch (err) {
      console.error('Error loading farm information:', err)
      setError('Failed to load farm information')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount).toFixed(2)}`
  }

  const formatDate = (dateString) => {
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  // ET-M1: Memoize due date calculation to avoid recalculating on every render
  const dueDate = useMemo(() => {
    if (!invoice?.date) return null
    const paymentTerms = customer?.payment_terms || 30
    const invoiceDate = parseDateString(invoice.date)
    const dueDate = new Date(invoiceDate)
    dueDate.setDate(dueDate.getDate() + parseInt(paymentTerms))
    return dueDate
  }, [invoice?.date, customer?.payment_terms])

  // EM-M2: Handle send with confirmation
  const handleSendClick = () => {
    setShowConfirmation(true)
  }

  const handleConfirmSend = () => {
    setShowConfirmation(false)
    onSendEmail()
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading email preview...</span>
        </CardContent>
      </Card>
    )
  }

  if (error || !farmInfo) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          {error || 'Unable to load farm information'}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-4">
      {/* Email Status Alert */}
      {sendStatus && (
        <Alert variant={sendStatus.success ? 'default' : 'destructive'}>
          {sendStatus.success ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          <AlertDescription>
            {sendStatus.success
              ? `Invoice email sent successfully to ${customer.email}`
              : `Failed to send email: ${sendStatus.error}`}
          </AlertDescription>
        </Alert>
      )}

      {/* Email Preview Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Invoice Email Preview
              </CardTitle>
              <CardDescription>
                Preview of email that will be sent to {customer.email}
              </CardDescription>
            </div>
            <Button
              onClick={handleSendClick}
              disabled={isSending || !customer.email}
              className="gap-2"
            >
              {isSending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  Send Invoice Email
                </>
              )}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* Email Template Preview */}
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            {/* Email Header */}
            <div className="mb-6 border-b border-gray-200 pb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {farmInfo.business_name}
                  </h1>
                  <div className="mt-2 space-y-1 text-sm text-gray-600">
                    {farmInfo.address_line_1 && <p>{farmInfo.address_line_1}</p>}
                    {farmInfo.address_line_2 && <p>{farmInfo.address_line_2}</p>}
                    {farmInfo.city && farmInfo.postcode && (
                      <p>
                        {farmInfo.city}, {farmInfo.postcode}
                      </p>
                    )}
                    {farmInfo.phone && <p>Tel: {farmInfo.phone}</p>}
                    {farmInfo.email && <p>Email: {farmInfo.email}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant="secondary" className="mb-2">
                    Invoice
                  </Badge>
                  <p className="text-lg font-semibold text-gray-900">
                    {invoice.invoice_no}
                  </p>
                  <p className="text-sm text-gray-600">
                    Date: {formatDate(invoice.date)}
                  </p>
                  {dueDate && (
                    <p className="text-sm text-gray-600">
                      Due: {formatDate(dueDate)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Email Body */}
            <div className="space-y-6">
              {/* Greeting */}
              <div>
                <p className="text-base text-gray-900">
                  Dear {customer.name},
                </p>
                <p className="mt-4 text-base text-gray-700">
                  Thank you for your business. Please find attached invoice{' '}
                  <span className="font-semibold">{invoice.invoice_no}</span> for the
                  total amount of{' '}
                  <span className="font-semibold">{formatCurrency(invoice.total)}</span>.
                </p>
              </div>

              {/* Invoice Summary */}
              <div className="rounded-lg bg-gray-50 p-4">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-700">
                  Invoice Summary
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Invoice Number:</span>
                    <span className="font-medium text-gray-900">
                      {invoice.invoice_no}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Invoice Date:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(invoice.date)}
                    </span>
                  </div>
                  {dueDate && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Due Date:</span>
                      <span className="font-medium text-gray-900">
                        {formatDate(dueDate)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-200 pt-2 text-base">
                    <span className="font-semibold text-gray-900">Total Amount:</span>
                    <span className="font-bold text-gray-900">
                      {formatCurrency(invoice.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Options Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Payment Options
                </h3>
                <p className="text-sm text-gray-600">
                  Choose your preferred payment method below:
                </p>

                {/* Option 1: Pay Online via Xero (Primary CTA) */}
                {invoice.online_payment_url && (
                  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                        <CreditCard className="h-5 w-5 text-green-700" />
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-1 font-semibold text-gray-900">
                          Pay Online
                        </h4>
                        <p className="mb-3 text-sm text-gray-600">
                          Pay securely with credit/debit card, Apple Pay, or Google Pay
                        </p>
                        <Button
                          asChild
                          className="gap-2"
                        >
                          <a
                            href={invoice.online_payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <CreditCard className="h-4 w-4" />
                            Pay Now
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Method Info - Show when GoCardless is configured */}
                {branch?.payment_method === 'gocardless' && (
                  <Alert className="mb-4 border-blue-200 bg-blue-50">
                    <AlertDescription className="text-sm text-gray-700">
                      <strong>Payment Method:</strong> Direct Debit (GoCardless) - Your payment will be collected automatically
                    </AlertDescription>
                  </Alert>
                )}

                {/* Option 2: Direct Debit via GoCardless (Wholesale only) */}
                {/* Architecture migration: customer_type moved to branch level */}
                {branch?.customer_type?.name === 'wholesale' && (
                  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                        <Repeat className="h-5 w-5 text-blue-700" />
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-1 font-semibold text-gray-900">
                          Set Up Direct Debit
                        </h4>
                        <p className="mb-1 text-sm text-gray-600">
                          Automate future payments with GoCardless (recommended for regular customers)
                        </p>
                        <p className="text-xs text-gray-500">
                          Contact us to set up direct debit for convenient automatic payments.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Option 3: Bank Transfer */}
                {farmInfo.bank_name && (
                  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                        <Banknote className="h-5 w-5 text-gray-700" />
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-1 font-semibold text-gray-900">
                          Bank Transfer
                        </h4>
                        <div className="mb-2 space-y-1 text-sm text-gray-700">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Bank:</span>
                            <span className="font-medium">{farmInfo.bank_name}</span>
                          </div>
                          {farmInfo.bank_sort_code && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Sort Code:</span>
                              <span className="font-medium font-mono">{farmInfo.bank_sort_code}</span>
                            </div>
                          )}
                          {farmInfo.bank_account_number && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Account:</span>
                              <span className="font-medium font-mono">{farmInfo.bank_account_number}</span>
                            </div>
                          )}
                          <div className="flex justify-between border-t border-gray-200 pt-2">
                            <span className="text-gray-600">Reference:</span>
                            <span className="font-semibold text-gray-900">{invoice.invoice_no}</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">
                          Please include the invoice number as your payment reference
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment reminder */}
                <div className="rounded-lg bg-gray-50 p-4">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Payment Due:</span>{' '}
                    {dueDate && formatDate(dueDate)} ({customer.payment_terms || 30} days)
                  </p>
                </div>
              </div>

              {/* Footer Message */}
              <div className="border-t border-gray-200 pt-6">
                <p className="text-sm text-gray-700">
                  If you have any questions about this invoice, please contact us at{' '}
                  {farmInfo.email || farmInfo.phone}.
                </p>
                <p className="mt-4 text-sm text-gray-700">
                  Thank you for your business!
                </p>
                <p className="mt-2 text-sm font-medium text-gray-900">
                  {farmInfo.business_name}
                </p>
              </div>
            </div>
          </div>

          {/* Email Metadata */}
          <div className="mt-4 rounded-lg bg-gray-50 p-4">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
              Email Details
            </h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">To:</span>
                <span className="font-medium text-gray-900">{customer.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">From:</span>
                <span className="font-medium text-gray-900">{farmInfo.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Subject:</span>
                <span className="font-medium text-gray-900">
                  Invoice {invoice.invoice_no} from {farmInfo.business_name}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Attachment:</span>
                <span className="font-medium text-gray-900">
                  {invoice.invoice_no}.pdf
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* EM-M2: Confirmation dialog before sending */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Invoice Email?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send invoice {invoice.invoice_no} to {customer.email} with payment instructions.
              The invoice PDF will be attached to the email.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSend}>
              Send Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
